import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatApiError } from "@/context/AuthContext";
import { UserPlus, Users } from "lucide-react";

// Lets PM add newly enrolled members at any point during Step 6 delivery — not tied
// to the step's Save Draft / Complete form, so it can be used repeatedly throughout
// the training. Each addition notifies IT to send those members access invites.
export default function NewMembersPanel({ programId, canEdit = true }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/programs/${programId}/new-members`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, [programId]);

  const add = async () => {
    const members = text.trim();
    if (!members) return;
    setAdding(true);
    try {
      await api.post(`/programs/${programId}/new-members`, { members });
      toast.success("Added — IT notified to send invites");
      setText("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="space-y-2">
          <Textarea
            data-testid="new-members-input"
            rows={2}
            placeholder="New member names/emails, comma-separated..."
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Button type="button" size="sm" data-testid="new-members-add" onClick={add} disabled={adding || !text.trim()}>
            <UserPlus className="w-4 h-4 mr-2" />
            {adding ? "Adding…" : "Add & Notify IT"}
          </Button>
        </div>
      )}
      {items.length === 0 && <div className="text-sm text-muted-foreground py-1 flex items-center gap-2"><Users className="w-4 h-4" /> No new members added yet.</div>}
      <div className="grid gap-2">
        {items.map((it) => (
          <div key={it.id} data-testid={`new-members-item-${it.id}`} className="p-3 border border-border rounded-md bg-white">
            <div className="text-sm">{it.members}</div>
            <div className="text-[11px] text-muted-foreground mt-1">Added by {it.added_by} · {new Date(it.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
